import type { ActivityItem, ActivitySections } from '../activity-journal';
import { presentationActivityJob } from '../activity-journal/job';
import {
    activityIntentFor,
    activitySectionFor,
    canDismissActivity,
} from '../activity-journal/policy';
import {
    activityBuildingContext,
    type ActivityBuildingContext,
    type ActivityBuildingDisplay,
} from './building-display';
import {
    activityIconContext,
    activityProgress,
    activityStageIcon,
    activityStatus,
    activityUpdatedLabel,
    isActivitySkipped,
    processingActivityTitle,
    toActivityAction,
} from './card-policy';
import { activityExportTitle, activityPreviewTitle } from './copy';
import {
    isDocumentBatchItem,
    toDocumentBatchView,
} from './document-batch/mapper';
import type {
    ActivityBuildingGroupView,
    ActivityPanelItem,
    ProcessingActivityCardView,
} from './models';

const EMPTY_BUILDING_LOOKUPS: ReadonlyMap<string, ActivityBuildingDisplay> =
    new Map();

export function mapActivityPanelGroups(
    sections: ActivitySections,
    now = Date.now(),
    buildingLookups = EMPTY_BUILDING_LOOKUPS
): readonly ActivityBuildingGroupView[] {
    const groups = new Map<string, ActivityPanelItem[]>();

    for (const item of toGroupedPanelItems(sections, now, buildingLookups)) {
        const groupId = item.buildingPublicId
            ? `building:${item.buildingPublicId}`
            : 'unidentified';
        groups.set(groupId, [...(groups.get(groupId) ?? []), item]);
    }

    return [...groups.entries()]
        .map(([id, items]): ActivityBuildingGroupView => {
            const sortedItems = [...items].sort(compareViewsNewestFirst);
            const newest = sortedItems[0];
            const building = newest.buildingPublicId
                ? buildingLookups.get(newest.buildingPublicId)
                : undefined;

            return {
                id,
                buildingPublicId: newest.buildingPublicId,
                buildingDetails: building?.details ?? null,
                buildingLocation: building?.location ?? null,
                buildingLabel: newest.buildingLabel,
                buildingAddress: newest.buildingAddress,
                newestAtUtc: newest.updatedAtUtc,
                items: sortedItems,
            };
        })
        .sort(
            (left, right) =>
                Date.parse(right.newestAtUtc) - Date.parse(left.newestAtUtc)
        );
}

function toGroupedPanelItems(
    sections: ActivitySections,
    now: number,
    buildingLookups: ReadonlyMap<string, ActivityBuildingDisplay>
): readonly ActivityPanelItem[] {
    const batchItems = new Map<string, ActivityItem[]>();
    const standaloneViews: ActivityPanelItem[] = [];

    for (const item of activityItems(sections)) {
        if (!isDocumentBatchItem(item)) {
            standaloneViews.push(toItemView(item, now, buildingLookups));
            continue;
        }

        const representative = presentationActivityJob(item);
        const batchIdentity =
            item.correlationId?.trim().toLowerCase() ||
            representative.jobId.trim().toLowerCase();
        batchItems.set(batchIdentity, [
            ...(batchItems.get(batchIdentity) ?? []),
            item,
        ]);
    }

    return [
        ...standaloneViews,
        ...[...batchItems.entries()].map(([batchIdentity, items]) =>
            toDocumentBatchView(batchIdentity, items, now, buildingLookups)
        ),
    ];
}

function activityItems(sections: ActivitySections): readonly ActivityItem[] {
    return [
        ...sections.inProgress,
        ...sections.needsAttention,
        ...sections.ready,
        ...sections.earlier,
    ];
}

function toItemView(
    item: ActivityItem,
    now: number,
    buildingLookups: ReadonlyMap<string, ActivityBuildingDisplay>
): ActivityPanelItem {
    const representative = presentationActivityJob(item);
    const section = activitySectionFor(item);
    const building = activityBuildingContext(item, buildingLookups);

    if (!item.key.startsWith('batch:') && representative.stage === 'preview') {
        const title =
            item.availability === 'unavailable'
                ? processingActivityTitle(item, representative)
                : activityPreviewTitle(section, isActivitySkipped(item));

        return {
            kind: 'preview',
            id: item.key,
            title,
            accessibleLabel: activityAccessibleLabel(item, title, building),
            ...buildingViewState(building),
            updatedAtUtc: item.updatedAtUtc,
            message: representative.message,
            icon: 'lucideEye',
            iconContext: activityIconContext(item, representative.stage),
            ...terminalCardState(item, now),
            progress: activityProgress(item, representative, title),
            action: toActivityAction(activityIntentFor(item), building.label),
        };
    }

    if (!item.key.startsWith('batch:') && representative.stage === 'export') {
        const title =
            item.availability === 'unavailable'
                ? processingActivityTitle(item, representative)
                : activityExportTitle(
                      section,
                      isActivitySkipped(item),
                      representative.exportKind
                  );

        return {
            kind: 'export',
            id: item.key,
            title,
            accessibleLabel: activityAccessibleLabel(item, title, building),
            ...buildingViewState(building),
            updatedAtUtc: item.updatedAtUtc,
            message: representative.message,
            icon: 'lucideUpload',
            iconContext: activityIconContext(item, representative.stage),
            ...terminalCardState(item, now),
            progress: activityProgress(item, representative, title),
            action: toActivityAction(activityIntentFor(item), building.label),
        };
    }

    return toProcessingView(item, now, building);
}

function toProcessingView(
    item: ActivityItem,
    now: number,
    building: ActivityBuildingContext
): ProcessingActivityCardView {
    const status = activityStatus(item);
    const representative = presentationActivityJob(item);
    const title = processingActivityTitle(item, representative);
    const progress = activityProgress(item, representative, title);

    return {
        kind: 'processing',
        id: item.key,
        title,
        accessibleLabel: activityAccessibleLabel(item, title, building),
        ...buildingViewState(building),
        updatedAtUtc: item.updatedAtUtc,
        updatedLabel: activityUpdatedLabel(item.updatedAtUtc, now),
        message: representative.message,
        icon: activityStageIcon(representative.stage),
        iconContext: activityIconContext(item, representative.stage),
        statusLabel: status.label,
        progress,
        primaryAction: toActivityAction(
            activityIntentFor(item),
            building.label
        ),
        dismissible: canDismissActivity(item),
    };
}

function activityAccessibleLabel(
    item: ActivityItem,
    title: string,
    building: ActivityBuildingContext
): string {
    return `${title} for ${building.label}; updated ${item.updatedAtUtc}; activity ${item.key}`;
}

function buildingViewState(building: ActivityBuildingContext) {
    return {
        buildingPublicId: building.buildingPublicId,
        buildingLabel: building.label,
        buildingAddress: building.address,
    };
}

function terminalCardState(item: ActivityItem, now: number) {
    const status = activityStatus(item);

    return {
        statusLabel: status.label,
        updatedLabel: activityUpdatedLabel(item.updatedAtUtc, now),
        dismissible: canDismissActivity(item),
    };
}

function compareViewsNewestFirst(
    left: ActivityPanelItem,
    right: ActivityPanelItem
): number {
    return Date.parse(right.updatedAtUtc) - Date.parse(left.updatedAtUtc);
}

function compareItemsNewestFirst(
    left: ActivityItem,
    right: ActivityItem
): number {
    return Date.parse(right.updatedAtUtc) - Date.parse(left.updatedAtUtc);
}
